import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const FirstLoginModal = ({ isVisible, onClose }) => {
  const { user, updateUser } = useAuth();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleClose = async () => {
    if (dontShowAgain) {
      try {
        // Update user preference to not show the guide again
        await api.post('/user/update-preferences', {
          showHowToPlay: false
        });
        
        // Update the user context immediately
        updateUser({ ...user, showHowToPlay: false });
      } catch (error) {
        console.error('Failed to update how-to-play preference:', error);
      }
    }
    onClose();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-600">
          <div>
            <h1 className="text-3xl font-bold text-cyan-400">🎮 Welcome to White Rabbit!</h1>
            <p className="text-gray-300 mt-2">Here's everything you need to know to get started</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white text-2xl"
          >
            ✕
          </button>
        </div>

        {/* How to Play Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-8">
            {/* Getting Started */}
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-4">🚀 Getting Started</h2>
              <div className="bg-gray-800 border border-green-400 p-6 rounded-lg space-y-3">
                <p><strong>💻 Deploy Computer:</strong> Click anywhere on the map to set your location (remember to zoom in to deploy)</p>
                <p><strong>🔄 Movement Tip:</strong> After moving your computer, it's good practice to click "Move Refresh" next to the map to update all features and users in your sniffer tool</p>
                <p><strong>🖱️ Activate Sniffer:</strong> Click your computer to reveal nearby targets (5km radius)</p>
                <p><strong>📡 Sniffer reveals:</strong> Treasure chests, banks, casinos, ATMs, vaults, users, houses, drones</p>
                <p><strong>🚁 Deploy Drones:</strong> Purchase drones from Parts for strategic operations</p>
                <p><strong>🍔 Movement System:</strong> Every time you move your computer, you consume 1 food/drink supply</p>
                <p><strong>🛒 Supply Management:</strong> Buy more supplies from shops or produce them at your own farm</p>
              </div>
            </section>

            {/* Financial Systems */}
            <section>
              <h2 className="text-2xl font-bold text-blue-400 mb-4">💰 Financial Systems</h2>
              <div className="bg-gray-800 border border-blue-400 p-6 rounded-lg space-y-3">
                <p><strong>🏦 Banks:</strong> Brute force attack to breach bank security systems</p>
                <p><strong>🎰 Casinos:</strong> Hack slot machines and vaults for massive rewards</p>
                <p><strong>🏧 ATMs:</strong> Use codes or brute force to withdraw funds</p>
                <p><strong>🔐 Personal Vaults:</strong> Store ATM codes and WR Coins, hackable by others for rewards</p>
                <p><strong>💳 Savings Accounts:</strong> Earn 5% APY on stored funds (can be hacked with scanning tools)</p>
              </div>
            </section>

            {/* SWAT System */}
            <section>
              <h2 className="text-2xl font-bold text-red-400 mb-4">🚨 SWAT System</h2>
              <div className="bg-gray-800 border border-red-400 p-6 rounded-lg space-y-3">
                <p><strong>⚠️ Warning:</strong> SWAT may respond to illegal activities!</p>
                <p><strong>🎯 Triggers:</strong> Successful hacks have a chance to alert authorities</p>
                <p><strong>🔒 Consequences:</strong> Getting caught results in temporary jail time</p>
                <p><strong>🚨 Report Attackers:</strong> If someone hacks you, report them to SWAT for immediate response</p>
                <p><strong>📋 SWAT Reports:</strong> Use the SWAT system to call authorities on criminals targeting you</p>
              </div>
            </section>

            {/* Tips & Strategy */}
            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">💡 Quick Tips</h2>
              <div className="bg-gray-800 border border-cyan-400 p-6 rounded-lg space-y-3">
                <p><strong>🛡️ Build defenses:</strong> Protect your assets with compound defense systems</p>
                <p><strong>💰 Diversify income:</strong> Balance treasure hunting, hacking, and real estate</p>
                <p><strong>⚡ Stay mobile:</strong> Move your computer strategically to access different targets</p>
                <p><strong>🤝 Form alliances:</strong> Work with other players for mutual protection</p>
                <p><strong>🍔 Manage supplies:</strong> Keep food/drink supplies stocked for computer movement</p>
              </div>
            </section>
          </div>
        </div>

        {/* Modal Footer with Checkbox */}
        <div className="p-6 border-t border-gray-600 bg-gray-900">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="dontShowAgain"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="w-4 h-4 text-cyan-400 bg-gray-700 border-gray-600 rounded focus:ring-cyan-400 focus:ring-2"
              />
              <label htmlFor="dontShowAgain" className="text-gray-300 text-sm">
                Don't show this guide at login anymore
              </label>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={handleClose}
                className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors"
              >
                {dontShowAgain ? 'Save & Close' : 'Close'}
              </button>
            </div>
          </div>
          <p className="text-gray-400 text-xs mt-2">
            You can always access the complete guide later from Profile → Game Guide
          </p>
        </div>
      </div>
    </div>
  );
};

export default FirstLoginModal;